import { css, html, LitElement, nothing } from "lit";

import { ltErrorText } from "../lib/errors-lt";
import { controlStyles } from "../styles/tokens";

/**
 * <modulina-change-password> — minimal change-password form (spec-web-ui
 * W2 SHOULD: employees get temp passwords via webuser.sh and need a UI to
 * replace them). Pure: emits `change-password`
 * (detail: { currentPassword, newPassword }); the shell calls
 * /api/auth/change-password and reflects busy/error/done back as props.
 */
export class ModulinaChangePassword extends LitElement {
  static override properties = {
    busy: { type: Boolean, attribute: false },
    errorCode: { attribute: false },
    done: { type: Boolean, attribute: false },
  };

  declare busy: boolean;
  declare errorCode: string | null;
  declare done: boolean;

  constructor() {
    super();
    this.busy = false;
    this.errorCode = null;
    this.done = false;
  }

  static override styles = [
    controlStyles,
    css`
      :host {
        display: block;
        background: var(--m-color-surface);
        border: 1px solid var(--m-color-border);
        border-radius: var(--m-radius);
        box-shadow: var(--m-shadow);
        padding: var(--m-space-4);
        max-width: 22rem;
      }
      h2 {
        font-size: var(--m-fs-base);
        margin: 0 0 var(--m-space-3);
      }
      .field {
        margin-bottom: var(--m-space-3);
      }
      .error {
        color: var(--m-color-danger);
        font-size: var(--m-fs-small);
        min-height: 1.2em;
        margin-bottom: var(--m-space-2);
      }
      .error .code {
        opacity: 0.75;
      }
      .ok {
        color: var(--m-color-ok);
        font-size: var(--m-fs-small);
        margin-bottom: var(--m-space-2);
      }
    `,
  ];

  override render() {
    return html`
      <h2>Slaptažodžio keitimas</h2>
      <form @submit=${this.onSubmit}>
        <div class="field">
          <label for="current">Dabartinis slaptažodis</label>
          <input
            id="current"
            name="currentPassword"
            type="password"
            autocomplete="current-password"
            required
          />
        </div>
        <div class="field">
          <label for="next">Naujas slaptažodis (bent 12 simbolių)</label>
          <input
            id="next"
            name="newPassword"
            type="password"
            autocomplete="new-password"
            minlength="12"
            required
          />
        </div>
        ${this.done ? html`<div class="ok" role="status">Slaptažodis pakeistas.</div>` : nothing}
        <div class="error" role="alert">
          ${this.errorCode
            ? html`${this.errorText(this.errorCode)}
                <span class="code">(kodas: ${this.errorCode})</span>`
            : nothing}
        </div>
        <button class="primary" type="submit" ?disabled=${this.busy}>
          ${this.busy ? "Keičiama…" : "Pakeisti slaptažodį"}
        </button>
      </form>
    `;
  }

  /** LOCAL gloss override (review minor 4): in THIS form
   * `invalid_credentials` means the CURRENT password was wrong — the
   * global gloss ("Neteisingas el. paštas arba slaptažodis.") talks about
   * an email field that does not exist here. The global map stays
   * untouched; the context-specific wording lives with the context. */
  private errorText(code: string): string {
    if (code === "invalid_credentials") return "Neteisingas dabartinis slaptažodis.";
    return ltErrorText(code);
  }

  private onSubmit(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    this.dispatchEvent(
      new CustomEvent("change-password", {
        detail: {
          currentPassword: String(data.get("currentPassword") ?? ""),
          newPassword: String(data.get("newPassword") ?? ""),
        },
        bubbles: true,
        composed: true,
      }),
    );
    form.reset();
  }
}

customElements.define("modulina-change-password", ModulinaChangePassword);
